// 근무스케줄 알림톡 발송 모달
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Stack,
  Box,
  Text,
  Checkbox,
  Flex,
  Divider,
  Grid,
  Heading,
  useToast,
  RadioGroup,
  Radio
} from '@chakra-ui/react';
import { getSemesterWorkers } from 'utils/firestoreUtils';

const NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER_URL || 'http://localhost:3001';

// 템플릿 정보
const TEMPLATES = {
  registration: {
    code: 'ppur_2026022319552151510528585',
    name: '근무스케줄 등록 안내',
    preview: (values) => `안녕하세요. ${values.name}님
근무스케줄이 등록되어 신청 가능함을 안내드립니다.
근무스케줄 신청 > 스케줄 조회 및 신청에서 확인할 수 있습니다.

■ 근무지 : ${values.semester}
■ 등록내역 : ${values.content}
■ 담당자 : ${values.manager}

추가 문의는 상기 담당자에게 연락 주시기 바랍니다.
감사합니다.`
  },
  confirmation: {
    code: 'ppur_2026022319521212712278587',
    name: '근무스케줄 확정 안내',
    preview: (values) => `안녕하세요. ${values.name}님
근무스케줄이 확정 및 마감되어 안내드립니다.
근무스케줄 신청 > 확정된 스케줄 에서 확인할 수 있습니다.

■ 근무지 : ${values.semester}
■ 마감내역 : ${values.content}
■ 담당자 : ${values.manager}

추가 문의는 상기 담당자에게 연락 주시기 바랍니다.
감사합니다.`
  }
};

function ScheduleAlimtalkModal({ isOpen, onClose, selectedSemester }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [templateType, setTemplateType] = useState('registration');
  const [formData, setFormData] = useState({
    content: '',
    manager: ''
  });

  // 근무자 목록 조회
  useEffect(() => {
    if (isOpen && selectedSemester) {
      loadWorkers();
    }
  }, [isOpen, selectedSemester]);

  const loadWorkers = async () => {
    if (!selectedSemester) return;
    
    try {
      setLoading(true);
      const data = await getSemesterWorkers(selectedSemester.id);
      
      // 실제 사용자 정보와 함께 저장
      const workersWithInfo = data.map(worker => ({
        id: worker.id,
        userId: worker.userId,
        name: worker.userName,
        phone: worker.userPhone,
        notificationConsent: worker.notificationConsent
      }));
      
      setWorkers(workersWithInfo);
    } catch (error) {
      console.error('근무자 목록 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '근무자 목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // 검색된 근무자
  const filteredWorkers = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return workers.filter(w => 
      w.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [workers, searchQuery]);

  // 발송 가능한 근무자 (전화번호 있고 알림 동의한 사람)
  const availableWorkers = useMemo(() => {
    return workers.filter(w => w.phone && w.notificationConsent);
  }, [workers]);

  // 전체 선택 토글
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedWorkerIds(availableWorkers.map(w => w.id));
    } else {
      setSelectedWorkerIds([]);
    }
  };

  // 개별 선택 토글
  const handleToggleWorker = (workerId, checked) => {
    if (checked) {
      setSelectedWorkerIds(prev => [...prev, workerId]);
    } else {
      setSelectedWorkerIds(prev => prev.filter(id => id !== workerId));
    }
  };

  // 미리보기 텍스트
  const previewText = useMemo(() => {
    const template = TEMPLATES[templateType];
    return template.preview({
      name: '[이름]',
      semester: selectedSemester?.semesterName || '[학기명]',
      content: formData.content || '[내역 입력 필요]',
      manager: formData.manager || '[담당자 입력 필요]'
    });
  }, [templateType, selectedSemester, formData]);

  // 알림톡 발송
  const handleSend = async () => {
    if (!formData.content.trim() || !formData.manager.trim()) {
      toast({
        title: '입력 오류',
        description: '내역과 담당자를 모두 입력해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    if (selectedWorkerIds.length === 0) {
      toast({
        title: '대상 없음',
        description: '발송할 근무자를 선택해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    const recipients = workers.filter(w => selectedWorkerIds.includes(w.id));
    const template = TEMPLATES[templateType];

    setSending(true);
    try {
      for (const worker of recipients) {
        const phoneNumber = worker.phone.replace(/\D/g, '');
        const variables = {
          '2': worker.name || '근무자',
          '3': selectedSemester?.semesterName || '',
          '4': formData.content,
          '5': formData.manager
        };

        const res = await fetch(`${NOTIFICATION_SERVER}/api/test/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phoneNumber,
            templateCode: template.code,
            name: worker.name || '근무자',
            variables,
          }),
        });

        const data = await res.json();
        if (!data.success) {
          throw new Error(data.error || `${worker.name || '근무자'}님 발송 실패`);
        }
      }

      toast({
        title: '알림톡 발송 완료',
        description: `${recipients.length}명에게 발송을 요청했습니다.`,
        status: 'success',
        duration: 4000
      });

      // 초기화
      setSelectedWorkerIds([]);
      setFormData({ content: '', manager: '' });
      setSearchQuery('');
      onClose();
    } catch (error) {
      console.error('알림톡 발송 오류:', error);
      toast({
        title: '알림톡 발송 실패',
        description: error.message || '알림톡 발송 중 문제가 발생했습니다.',
        status: 'error',
        duration: 4000
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedWorkerIds([]);
    setFormData({ content: '', manager: '' });
    setSearchQuery('');
    setTemplateType('registration');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>근무스케줄 알림톡 발송</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid templateColumns={{ base: '1fr', md: '320px 1fr' }} gap={6}>
            {/* 왼쪽: 알림톡 미리보기 */}
            <Box>
              <Heading size="sm" mb={3}>알림톡 미리보기</Heading>
              <Box 
                borderWidth="1px" 
                borderRadius="md" 
                p={4} 
                bg="gray.50" 
                _dark={{ bg: 'gray.700' }}
                minH="400px"
              >
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {previewText}
                </Text>
              </Box>
            </Box>

            {/* 오른쪽: 발송 설정 */}
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">템플릿 선택</FormLabel>
                <RadioGroup value={templateType} onChange={setTemplateType}>
                  <Stack>
                    <Radio value="registration">근무스케줄 등록 안내</Radio>
                    <Radio value="confirmation">근무스케줄 확정 안내</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <FormControl isReadOnly>
                <FormLabel fontSize="sm">학기명 [*3*]</FormLabel>
                <Input 
                  value={selectedSemester?.semesterName || ''} 
                  isReadOnly
                  bg="gray.100"
                  _dark={{ bg: 'gray.700' }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">
                  {templateType === 'registration' ? '등록내역 [*4*]' : '마감내역 [*4*]'}
                </FormLabel>
                <Input
                  placeholder="예: 3월 근무스케줄"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">담당자 [*5*]</FormLabel>
                <Input
                  placeholder="예: 홍길동 (051-510-3750)"
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                />
              </FormControl>

              <Divider />

              <Box>
                <Heading size="sm" mb={2}>발송 대상 선택</Heading>
                <Stack spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">이름 검색</FormLabel>
                    <Input
                      placeholder="이름으로 검색"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </FormControl>

                  {loading ? (
                    <Text fontSize="sm" color="gray.500">근무자 목록을 불러오는 중...</Text>
                  ) : searchQuery.trim() ? (
                    filteredWorkers.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">검색 결과가 없습니다.</Text>
                    ) : (
                      <Box borderWidth="1px" borderRadius="md" maxH="240px" overflowY="auto">
                        <Stack spacing={0} divider={<Divider />}>
                          {filteredWorkers.map(worker => {
                            const disabled = !worker.phone || !worker.notificationConsent;
                            const disabledReason = !worker.phone 
                              ? '전화번호 없음' 
                              : !worker.notificationConsent 
                              ? '알림 수신 미동의' 
                              : '';
                            
                            return (
                              <Flex
                                key={worker.id}
                                align="center"
                                justify="space-between"
                                px={3}
                                py={2}
                                opacity={disabled ? 0.6 : 1}
                              >
                                <Checkbox
                                  isChecked={selectedWorkerIds.includes(worker.id)}
                                  onChange={(e) => handleToggleWorker(worker.id, e.target.checked)}
                                  isDisabled={disabled}
                                >
                                  <Box>
                                    <Text fontWeight="bold">{worker.name || '이름 없음'}</Text>
                                    {disabled && disabledReason && (
                                      <Text fontSize="xs" color="red.500">
                                        ({disabledReason})
                                      </Text>
                                    )}
                                  </Box>
                                </Checkbox>
                                <Text fontSize="sm">{worker.phone || '전화번호 없음'}</Text>
                              </Flex>
                            );
                          })}
                        </Stack>
                      </Box>
                    )
                  ) : (
                    <Box>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Checkbox
                          isChecked={selectedWorkerIds.length === availableWorkers.length && availableWorkers.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        >
                          <Text fontWeight="bold">전체 선택</Text>
                        </Checkbox>
                        <Text fontSize="sm" color="gray.600">
                          발송 가능: <Text as="span" fontWeight="bold">{availableWorkers.length}</Text>명 
                          / 전체: {workers.length}명
                        </Text>
                      </Flex>
                      <Text fontSize="xs" color="gray.500">
                        전화번호가 없거나 알림 수신 미동의한 인원은 자동으로 제외됩니다.
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            취소
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleSend}
            isLoading={sending}
            isDisabled={loading || !formData.content.trim() || !formData.manager.trim()}
          >
            {`발송 (${selectedWorkerIds.length}명)`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ScheduleAlimtalkModal;
